import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { CustomerSubscriptionResponseModel } from 'src/app/models/response/customer-subscription-response';
import {
  CustomerSubscriptionRequestModel,
  GetCustomerSubscriptionsRequestModel
} from 'src/app/models/request/customer-subscription-request';

interface SubscriptionRecord {
  id: string;
  subscriptionId: number;
  start: string;
  end: string;
  totalVolume: number;
  currentUsage: number;
  remaining: number;
  percent: number;
  status: string;
}

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.sass']
})
export class SubscriptionsComponent implements OnInit, OnDestroy {
  private unsubscriber = new Subject<boolean>();

  displayNewSubscriptionDialog = false;
  SubscriptionDeletePopup = false;
  loading = false;

  customerId: number = null;
  customerName = '';
  private preselectId: number = null;

  subscriptions: SubscriptionRecord[] = [];
  selectedSubscription: SubscriptionRecord = null;

  newSubscription = {
    customerId: null as number,
    startDate: '',
    endDate: '',
    volume: null as number
  };
  Menustatus: boolean;

  constructor(private route: ActivatedRoute, private userService: UserService) { }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(params => {
        this.customerId = params.get('customerId') ? Number(params.get('customerId')) : null;
        this.customerName = params.get('name') || '';
        this.preselectId = params.get('subscriptionId') ? Number(params.get('subscriptionId')) : null;
        this.loadSubscriptions();
      });
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  loadSubscriptions(): void {
    if (!this.customerId) {
      this.subscriptions = [];
      this.selectedSubscription = null;
      return;
    }
    const req = new GetCustomerSubscriptionsRequestModel();
    req.CustomerId = this.customerId;
    req.PageNumber = 1;
    req.PageSize = 1000;

    this.loading = true;
    this.userService.getCustomerSubscriptions(req)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((res: any) => {
        const raw: any = res?.ResponseModel?.Data ?? res?.ResponseModel ?? res?.Data?.Data;
        const data: CustomerSubscriptionResponseModel[] = Array.isArray(raw) ? raw : [];
        const nameKey = (this.customerName || '').trim().toLowerCase();
        this.subscriptions = data
          .filter(s =>
            String(s.CompanyId) === String(this.customerId) ||
            (!!nameKey && (s.FullName || '').trim().toLowerCase() === nameKey))
          .map(s => this.mapRecord(s));
        if (!this.customerName && data.length) {
          this.customerName = data[0].FullName || '';
        }
        const order = { Active: 0, Upcoming: 1, Expired: 2 };
        this.selectedSubscription =
          this.subscriptions.find(s => s.subscriptionId === this.preselectId) ||
          this.subscriptions.slice().sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9))[0] ||
          null;
      });
  }

  private mapRecord(s: CustomerSubscriptionResponseModel): SubscriptionRecord {
    const total = Number(s.SubscriptionVolume) || 0;
    const used = Number(s.ConsumedVolume) || 0;
    return {
      id: s.SubscriptionCode || String(s.SubscriptionId),
      subscriptionId: s.SubscriptionId,
      start: s.StartDate as any,
      end: s.EndDate as any,
      totalVolume: total,
      currentUsage: used,
      remaining: s.RemainingVolume != null ? Number(s.RemainingVolume) : Math.max(total - used, 0),
      percent: s.UsagePercent != null ? Number(s.UsagePercent) : (total > 0 ? Math.round((used / total) * 100) : 0),
      status: s.Status
    };
  }

  selectSubscription(subscription: SubscriptionRecord): void {
    this.selectedSubscription = subscription;
  }

  get usagePercent(): number {
    if (!this.selectedSubscription) { return 0; }
    return Math.min(Math.round(this.selectedSubscription.percent || 0), 100);
  }

  get remainingVolume(): number {
    return this.selectedSubscription ? this.selectedSubscription.remaining : 0;
  }

  statusClass(status: string): string {
    return (status || '').toLowerCase();
  }

  openNewSubscriptionDialog(): void {
    this.newSubscription = { customerId: this.customerId, startDate: '', endDate: '', volume: null };
    this.displayNewSubscriptionDialog = true;
  }

  closeNewSubscriptionDialog(): void {
    this.displayNewSubscriptionDialog = false;
  }

  createSubscription(): void {
    if (!this.customerId || !this.newSubscription.startDate || !this.newSubscription.endDate || !this.newSubscription.volume) {
      return;
    }
    const req = new CustomerSubscriptionRequestModel();
    req.SubscriptionId = 0;
    req.CustomerId = this.customerId;
    req.StartDate = this.newSubscription.startDate as any;
    req.EndDate = this.newSubscription.endDate as any;
    req.SubscriptionVolume = String(this.newSubscription.volume) as any;

    this.loading = true;
    this.userService.createUpdateCustomerSubscription(req)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe(() => {
        this.closeNewSubscriptionDialog();
        this.loadSubscriptions();
      });
  }

  closeDeletePopup(): void {
    this.SubscriptionDeletePopup = false;
  }

  onClickYes(): void {
    if (this.selectedSubscription) {
      this.subscriptions = this.subscriptions.filter(sub => sub.id !== this.selectedSubscription.id);
      this.selectedSubscription = this.subscriptions[0] || null;
    }
    this.closeDeletePopup();
  }


   showMenu() {
    this.Menustatus = !this.Menustatus;
  }
}
